import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Visual Sweeps — FTUE demo (3 essais progressive intro).
 *
 * Spec : wiki/games/visual-sweeps.md §FTUE
 *   E1 : 80% cohérence, durée longue (1500 ms). Mouvement évident.
 *   E2 : 50% cohérence, durée moyenne. Filtrage léger.
 *   E3 : 30% cohérence — calibration baseline.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DIRECTIONS, DIRECTION_LABELS, } from './params';
const DEMO_STEPS = [
    {
        idx: 1,
        titleKey: 'visual_sweeps.demo.step1_title',
        hintKey: 'visual_sweeps.demo.step1_hint',
        direction: 'right',
        coherence: 0.8,
        durationMs: 1500,
    },
    {
        idx: 2,
        titleKey: 'visual_sweeps.demo.step2_title',
        hintKey: 'visual_sweeps.demo.step2_hint',
        direction: 'up',
        coherence: 0.5,
        durationMs: 1200,
    },
    {
        idx: 3,
        titleKey: 'visual_sweeps.demo.step3_title',
        hintKey: 'visual_sweeps.demo.step3_hint',
        direction: 'left',
        coherence: 0.3,
        durationMs: 1000,
    },
];
const FIELD = 240;
const DOT_R = 2;
const SPEED = 1.2;
export function Demo({ level: _level, onComplete, onSkip }) {
    const { t } = useTranslation(['games', 'common']);
    const [stepIdx, setStepIdx] = useState(0);
    const [phase, setPhase] = useState('showing');
    const [pick, setPick] = useState(null);
    const canvasRef = useRef(null);
    const rafRef = useRef(null);
    const showTimerRef = useRef(null);
    const step = DEMO_STEPS[stepIdx];
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    const correct = pick === step.direction;
    useEffect(() => {
        if (phase !== 'showing')
            return;
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return;
        const dots = [];
        const dirVec = (() => {
            if (step.direction === 'up')
                return { vx: 0, vy: -SPEED };
            if (step.direction === 'down')
                return { vx: 0, vy: SPEED };
            if (step.direction === 'left')
                return { vx: -SPEED, vy: 0 };
            return { vx: SPEED, vy: 0 };
        })();
        for (let i = 0; i < 100; i += 1) {
            const coherent = Math.random() < step.coherence;
            if (coherent) {
                dots.push({
                    x: Math.random() * FIELD,
                    y: Math.random() * FIELD,
                    vx: dirVec.vx,
                    vy: dirVec.vy,
                });
            }
            else {
                const a = Math.random() * Math.PI * 2;
                dots.push({
                    x: Math.random() * FIELD,
                    y: Math.random() * FIELD,
                    vx: Math.cos(a) * SPEED,
                    vy: Math.sin(a) * SPEED,
                });
            }
        }
        let running = true;
        const draw = () => {
            if (!running)
                return;
            ctx.fillStyle = '#1a1a1a';
            ctx.fillRect(0, 0, FIELD, FIELD);
            ctx.fillStyle = '#f5f1e8';
            for (const d of dots) {
                d.x += d.vx;
                d.y += d.vy;
                if (d.x < 0)
                    d.x += FIELD;
                else if (d.x > FIELD)
                    d.x -= FIELD;
                if (d.y < 0)
                    d.y += FIELD;
                else if (d.y > FIELD)
                    d.y -= FIELD;
                ctx.beginPath();
                ctx.arc(d.x, d.y, DOT_R, 0, Math.PI * 2);
                ctx.fill();
            }
            rafRef.current = requestAnimationFrame(draw);
        };
        rafRef.current = requestAnimationFrame(draw);
        showTimerRef.current = setTimeout(() => {
            setPhase('awaiting');
        }, step.durationMs);
        return () => {
            running = false;
            if (rafRef.current !== null)
                cancelAnimationFrame(rafRef.current);
            if (showTimerRef.current !== null)
                clearTimeout(showTimerRef.current);
        };
    }, [phase, step]);
    const handlePick = (d) => {
        if (phase !== 'awaiting')
            return;
        setPick(d);
        setPhase('feedback');
    };
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
        setPhase('showing');
        setPick(null);
    };
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: t('common:demo.eyebrow_with_master', { master: t('visual_sweeps.demo.master') }) }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: t(step.titleKey) }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: t(step.hintKey) }), _jsxs("div", { style: { width: FIELD, height: FIELD, background: '#1a1a1a' }, children: [phase === 'showing' && (_jsx("canvas", { ref: canvasRef, width: FIELD, height: FIELD })), phase !== 'showing' && (_jsx("div", { className: "flex h-full w-full items-center justify-center", children: _jsx("span", { className: "font-display text-[18px] text-cyan-surface", children: phase === 'awaiting' ? t('visual_sweeps.demo.ask_dominant') : correct ? '✓' : '✗' }) }))] }), phase === 'awaiting' && (_jsx("div", { className: "grid grid-cols-4 gap-s3", children: DIRECTIONS.map((d) => (_jsx("button", { type: "button", onClick: () => handlePick(d), "aria-label": DIRECTION_LABELS[d], className: "flex h-[56px] w-[56px] items-center justify-center rounded-full border border-cyan-border bg-cyan-surface text-[26px] text-cyan-text transition duration-feedback ease-soft-wind hover:scale-110 hover:bg-cyan-bg active:scale-95", children: DIRECTION_LABELS[d] }, d))) })), phase === 'feedback' && (_jsx("p", { className: [
                    'text-[13px]',
                    correct ? 'text-cyan-core' : 'text-cyan-media',
                ].join(' '), children: correct
                    ? t('visual_sweeps.demo.feedback_correct', { direction: DIRECTION_LABELS[step.direction] })
                    : t('visual_sweeps.demo.feedback_incorrect', { direction: DIRECTION_LABELS[step.direction] }) })), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [phase === 'feedback' && (_jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? t('common:actions.start_session') : t('common:actions.next_trial') })), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.skip_demo') })] })] }));
}
//# sourceMappingURL=demo.js.map